import { Box, CircularProgress } from '@mui/material';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import type { Permission } from '@/types';

interface ProtectedRouteProps {
  /** Every listed permission is required. */
  requires?: Permission[];
}

/**
 * Route-level gate. It only decides what to render — the backend re-checks every request, so a
 * user who reaches a page by URL still cannot read data they are not entitled to.
 */
export function ProtectedRoute({ requires = [] }: ProtectedRouteProps): JSX.Element {
  const { isAuthenticated, isLoading, can } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <CircularProgress aria-label="Loading your account" />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }

  if (requires.length > 0 && !can(...requires)) return <Navigate to="/not-authorized" replace />;

  return <Outlet />;
}
